/** Build structured on-page issue rows for URL detail Technical section. */

export interface OnpageIssueView {
  title: string;
  details: string[];
}

interface StoredPageResource {
  url: string;
  resourceType: 'script' | 'stylesheet';
}

interface PageResourcesPayload {
  renderBlockingScripts?: StoredPageResource[];
  renderBlockingStylesheets?: StoredPageResource[];
  metaCounts?: {
    renderBlockingScripts?: number;
    renderBlockingStylesheets?: number;
  };
  totals?: { scripts?: number; stylesheets?: number };
}

function pageDataFromAnalysis(analysis: unknown): Record<string, unknown> | null {
  const a = analysis as { rawData?: { pageData?: unknown } } | null;
  const pd = a?.rawData?.pageData;
  return pd && typeof pd === 'object' ? (pd as Record<string, unknown>) : null;
}

function meta(pageData: Record<string, unknown> | null): Record<string, unknown> {
  const m = pageData?.['meta'];
  return m && typeof m === 'object' ? (m as Record<string, unknown>) : {};
}

function checks(pageData: Record<string, unknown> | null): Record<string, boolean> {
  const c = pageData?.['checks'];
  if (!c || typeof c !== 'object') return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(c as Record<string, unknown>)) {
    if (typeof v === 'boolean') out[k] = v;
  }
  return out;
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v).trim()).filter(Boolean);
}

function pageResourcesFromAnalysis(analysis: unknown): PageResourcesPayload | null {
  const pr = (analysis as { rawData?: { pageResources?: unknown } } | null)?.rawData?.pageResources;
  return pr && typeof pr === 'object' ? (pr as PageResourcesPayload) : null;
}

function renderBlockingResourceDetails(analysis: unknown): string[] {
  const payload = pageResourcesFromAnalysis(analysis);
  if (!payload) return [];

  const details: string[] = [];
  const scripts = payload.renderBlockingScripts ?? [];
  const styles = payload.renderBlockingStylesheets ?? [];

  for (const row of scripts) {
    if (row?.url) details.push(`Script: ${row.url}`);
  }
  for (const row of styles) {
    if (row?.url) details.push(`Stylesheet: ${row.url}`);
  }

  const metaScripts = payload.metaCounts?.renderBlockingScripts;
  const metaStyles = payload.metaCounts?.renderBlockingStylesheets;
  if (
    typeof metaScripts === 'number' &&
    scripts.length > 0 &&
    scripts.length < metaScripts
  ) {
    details.push(
      `DataForSEO reports ${metaScripts} blocking script${metaScripts === 1 ? '' : 's'}; showing ${scripts.length} earliest-loaded match${scripts.length === 1 ? '' : 'es'}.`
    );
  }
  if (
    typeof metaStyles === 'number' &&
    styles.length > 0 &&
    styles.length < metaStyles
  ) {
    details.push(
      `DataForSEO reports ${metaStyles} blocking stylesheet${metaStyles === 1 ? '' : 's'}; showing ${styles.length} earliest-loaded match${styles.length === 1 ? '' : 'es'}.`
    );
  }

  if (details.length === 0 && (metaScripts || metaStyles)) {
    details.push(
      'Render-blocking counts are set on the page, but no matching script/stylesheet URLs were returned for this crawl.'
    );
  }

  return details;
}

function resourceErrorViews(pageData: Record<string, unknown> | null, kind: 'errors' | 'warnings'): OnpageIssueView[] {
  const re = pageData?.['resource_errors'];
  if (!re || typeof re !== 'object') return [];
  const list = (re as Record<string, unknown>)[kind];
  if (!Array.isArray(list)) return [];
  const views: OnpageIssueView[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const message = typeof row['message'] === 'string' ? row['message'].trim() : '';
    if (!message) continue;
    const details: string[] = [];
    const line = Number(row['line']);
    const column = Number(row['column']);
    if (Number.isFinite(line) && line > 0) {
      details.push(`Line ${line}${Number.isFinite(column) && column > 0 ? `, column ${column}` : ''}`);
    }
    views.push({ title: message, details });
  }
  return views;
}

function checkIssueViews(
  pageData: Record<string, unknown> | null,
  analysis: unknown
): {
  critical: OnpageIssueView[];
  warnings: OnpageIssueView[];
} {
  const c = checks(pageData);
  const m = meta(pageData);
  const critical: OnpageIssueView[] = [];
  const warnings: OnpageIssueView[] = [];

  const pushCritical = (title: string, details: string[] = []) => critical.push({ title, details });
  const pushWarning = (title: string, details: string[] = []) => warnings.push({ title, details });

  if (c['https_to_http_links']) {
    pushCritical('HTTPS page contains HTTP links', ['Mixed content / insecure links on an HTTPS URL.']);
  }
  if (c['has_render_blocking_resources']) {
    const scripts = Number(m['render_blocking_scripts_count']);
    const styles = Number(m['render_blocking_stylesheets_count']);
    const resourceDetails = renderBlockingResourceDetails(analysis);
    const details: string[] = resourceDetails.length
      ? resourceDetails
      : [
          ...(Number.isFinite(scripts)
            ? [`${scripts} render-blocking script${scripts === 1 ? '' : 's'}`]
            : []),
          ...(Number.isFinite(styles)
            ? [`${styles} render-blocking stylesheet${styles === 1 ? '' : 's'}`]
            : []),
          'Re-run OnPage analysis to fetch per-file URLs (Resources API).'
        ];
    pushCritical('Page has render-blocking resources', details);
  }
  if (c['duplicate_meta_tags']) {
    const tags = stringList(m['duplicate_meta_tags']);
    pushCritical(
      'Duplicate meta tags',
      tags.length ? tags.map((t) => `Duplicate: ${t}`) : ['Duplicate meta tag types detected.']
    );
  }
  if (c['no_image_alt']) pushCritical('Images missing alt text');
  if (c['no_image_title']) pushCritical('Images missing title attributes');
  if (c['frame']) pushCritical('Page uses frames');
  if (c['is_4xx_code']) pushCritical('Page returns 4xx status code');
  if (c['is_5xx_code']) pushCritical('Page returns 5xx status code');
  if (c['is_broken']) pushCritical('Page is broken (error status code)');
  if (c['high_loading_time']) pushCritical('High loading time (>3s)');
  if (c['high_waiting_time']) pushCritical('High waiting time (>1.5s)');
  if (c['size_greater_than_3mb']) pushCritical('Page size exceeds 3MB');
  if (c['no_h1_tag']) pushCritical('Page missing H1 tag');
  if (c['title_too_long']) pushCritical('Page title too long (>65 characters)');
  if (c['title_too_short']) pushCritical('Page title too short (<30 characters)');
  if (c['no_title']) pushCritical('Page missing title tag');
  if (c['no_description']) pushCritical('Page missing meta description');
  if (c['no_favicon']) pushCritical('Page missing favicon');
  if (c['duplicate_title_tag']) pushCritical('Page has multiple title tags');
  if (c['irrelevant_title']) pushCritical('Page title irrelevant to content');
  if (c['irrelevant_description']) pushCritical('Page description irrelevant to content');
  if (c['low_content_rate']) pushCritical('Low content rate (<10% text)');
  if (c['low_character_count']) pushCritical('Low character count (<1024 characters)');
  if (c['low_readability_rate']) pushCritical('Low readability score (Flesch-Kincaid <15)');
  if (!c['has_meta_title']) pushCritical('Page missing meta title tag');

  if (c['deprecated_html_tags']) {
    const tags = stringList(m['deprecated_tags']);
    pushWarning(
      'Deprecated HTML tags',
      tags.length ? tags.map((t) => `Tag: ${t}`) : ['Deprecated HTML elements detected.']
    );
  }
  if (!c['has_micromarkup']) {
    pushWarning('Page missing structured data (schema / microdata)');
  }

  return { critical, warnings };
}

function fallbackFromStrings(items: string[]): OnpageIssueView[] {
  return items.map((title) => ({ title, details: [] }));
}

/** Prefer structured raw crawl data; fall back to persisted string lists. */
export function buildOnpageTechnicalIssueViews(analysis: unknown): {
  critical: OnpageIssueView[];
  warnings: OnpageIssueView[];
} {
  const pageData = pageDataFromAnalysis(analysis);
  if (pageData) {
    const fromChecks = checkIssueViews(pageData, analysis);
    return {
      critical: [...resourceErrorViews(pageData, 'errors'), ...fromChecks.critical],
      warnings: [...resourceErrorViews(pageData, 'warnings'), ...fromChecks.warnings]
    };
  }

  const a = analysis as { criticalIssues?: string[]; warnings?: string[] } | null;
  return {
    critical: fallbackFromStrings(Array.isArray(a?.criticalIssues) ? a!.criticalIssues! : []),
    warnings: fallbackFromStrings(Array.isArray(a?.warnings) ? a!.warnings! : [])
  };
}
